import moment from "moment"
import {
    Charity,
    DistributionType,
    emptyFiduciarySummary,
    EstateFlowchartState,
    EstateToggle,
    FiduciaryAssignment,
    FiduciaryMemberType,
    FiduciarySummary,
    MemberType,
    Outright,
    RevocableTrust,
    Trust,
    Trustee
} from "../model/EstateFlowchartTypes"
import {LifeStatus} from "src/ClientManagement/models/MemberType"
import {MemberGroup} from "src/ClientManagement/models/InvestorGroupType"
import {FamilyRelationshipType} from "src/ClientManagement/models/FamilyRelationshipType"
import {PoaType} from "../components/PowersOfAppointmentSection/PoaType";
import {TransferType} from "../components/PowersOfAppointmentSection/PoaTransferType";
import {PowerOfAppointment} from "../components/PowersOfAppointmentSection/PowerOfAppointment";
import {IrrevocableTrust} from '../model/IrrevocableTrust';
import {Will} from "../model/Will"
import {EstateCardViewModeType} from "../components/EstateCard/EstateCard";
import {Miscellaneous} from "../model/Miscellaneous"

export const headers = new Headers({
    'Content-Type': 'application/json',
    Accept: '*/*',
    'trace-id': 'traceId'
});

/**
 * Generates an array of `EstateFlowchartState` objects for testing purposes.
 *
 * @param overrides - Optional partial overrides to apply to each generated `EstateFlowchartState`.
 * @param numberOfFlowcharts - Optional number of flowcharts to generate. If not provided, a single flowchart is generated.
 * @returns An array of `EstateFlowchartState` objects, each with a unique `flowchartId` and optionally overridden properties.
 */
export function generateEstateFlowcharts(
    overrides?: Partial<EstateFlowchartState>,
    numberOfFlowcharts?: number
): EstateFlowchartState[] {
    if (numberOfFlowcharts !== undefined) {
        return Array.from({length: numberOfFlowcharts}, (_, i) => ({
            flowchartId: `flowchart-id-${i + 1}`,
            flowchartName: `Sample Flowchart-${i + 1}`,
            revocableTrust: generateRevocableTrust({flowchartName: `Sample Flowchart-${i + 1}`, ...overrides?.revocableTrust}),
            viewMode: EstateCardViewModeType.SIMPLE,
            estateTaxDueTypes: [],
            ...overrides
        }));
    } else {
        return [{
            flowchartId: "flowchart-id",
            flowchartName: "Sample Flowchart",
            revocableTrust: generateRevocableTrust(),
            viewMode: EstateCardViewModeType.SIMPLE,
            estateTaxDueTypes: [],
            ...overrides
        }]
    }
}

export function generateEstateFlowchartsWithIrrev(
    overrides?: Partial<EstateFlowchartState>,
): EstateFlowchartState[] {
    return [{
        flowchartId: "flowchart-id",
        irrevocableTrusts: [generateIrrevocableTrust()],
        willSummary: {primaryWill: undefined, partnerWill: undefined},
        viewMode: EstateCardViewModeType.SIMPLE,
        estateToggle: EstateToggle.FULL_ESTATE,
        estateTaxDueTypes: [],
        ...overrides
    }]
}

export function generateEstateFlowchartsWithWill(
    overrides?: Partial<EstateFlowchartState>,
): EstateFlowchartState[] {
    return [{
        flowchartId: "flowchart-id",
        willSummary: {primaryWill: generateWill(), partnerWill: undefined},
        viewMode: EstateCardViewModeType.SIMPLE,
        estateTaxDueTypes: [],
        ...overrides
    }]
}

export function generateEstateFlowchartsWithDistributions(overrides?: Partial<EstateFlowchartState>): EstateFlowchartState[] {
    return [{
        flowchartId: "flowchart-id",
        revocableTrust: generateRevocableTrustWithFirstDeathAndRemainder(),
        viewMode: EstateCardViewModeType.SIMPLE,
        estateTaxDueTypes: [],
        ...overrides
    }]
}

export function generateEstateFlowchartsWithRevAndWill(overrides?: Partial<EstateFlowchartState>): EstateFlowchartState[] {
    return [{
        flowchartId: "flowchart-id",
        revocableTrust: generateRevocableTrustWithFirstDeathAndRemainder(),
        viewMode: EstateCardViewModeType.SIMPLE,
        estateToggle: EstateToggle.FULL_ESTATE,
        willSummary: {
            primaryWill: {...generateWill(), trustId: "primary-will-id"},
            partnerWill: {...generateWill(), trustId: "partner-will-id"},
        },
        ...overrides
    }]
}

export function generateEstateFlowchartWithMiscellaneous(overrides?: Partial<EstateFlowchartState>): EstateFlowchartState[] {
    return [{
        flowchartId: "flowchart-id",
        wildcardTrusts: [generateMiscellaneous()],
        estateToggle: EstateToggle.FULL_ESTATE,
        ...overrides
    }]
}

/**
 * Generates an array containing a single empty `EstateFlowchartState` object.
 * Allows optional overrides for the default properties.
 *
 * @param overrides - Partial properties to override the default `EstateFlowchartState` values.
 * @returns An array with one `EstateFlowchartState` object, with optional overrides applied.
 */
export function generateEmptyEstateFlowcharts(overrides?: Partial<EstateFlowchartState>): EstateFlowchartState[] {
    return [{
        flowchartId: "flowchart-id",
        revocableTrust: generateEmptyRevocableTrust(),
        viewMode: EstateCardViewModeType.SIMPLE,
        estateTaxDueTypes: [],
        flowchartName: "Sample Flowchart",
        ...overrides
    }]
}

export function generateRevocableTrust(overrides?: Partial<RevocableTrust>): RevocableTrust {
    return {
        flowchartName: "Sample Flowchart",
        legalName: "Sample Legal Name",
        nickName: "Sample nickname",
        trustCreationDate: moment("2024-09-01").toISOString(),
        recordOfAmendment: "Sample Record of Amendments",
        trustId: "some-revtrust-id",
        grantors: [
            {
                trustId: "some-revtrust-id",
                memberId: testGetFamilyMockData.primaryMember.id,
                memberType: MemberType.Grantor,
                familyMember: true
            },
            {
                trustId: "some-revtrust-id",
                memberId: testGetFamilyMockData.partnerMember?.id,
                memberType: MemberType.Grantor,
                familyMember: true
            }
        ],
        trustees: [
            {
                trustId: "some-revtrust-id",
                memberId: testGetFamilyMockData.primaryMember.id,
                memberType: MemberType.Trustee,
                familyMember: true
            },
            {
                trustId: "some-revtrust-id",
                memberId: testGetFamilyMockData.partnerMember?.id,
                memberType: MemberType.Trustee,
                familyMember: true
            }
        ],
        successors: [
            {
                customName: "",
                trustId: "some-revtrust-id",
                memberId: testGetFamilyMockData.primaryMember.id,
                memberType: MemberType.Successor,
                familyMember: true,
                memberOrder: 0
            },
            {
                customName: "",
                trustId: "some-revtrust-id",
                memberId: testGetFamilyMockData.partnerMember?.id,
                memberType: MemberType.Successor,
                familyMember: true,
                memberOrder: 1
            },
            {
                customName: "",
                trustId: "some-revtrust-id",
                memberId: testGetFamilyMockData.additionalMembers[0].id,
                memberType: MemberType.Successor,
                familyMember: true,
                memberOrder: 2
            }
        ],
        funding: "Sample funding",
        investmentAdvisor: "investment advisor",
        trustProtector: "trust protector",
        trustJurisdiction: "NY",
        assumedFirstDeath: testGetFamilyMockData.primaryMember.id,
        hasBeneficiariesAtFirstDeath: true,
        beneficiaries: {
            firstDeathDistributions: [generateTrust(DistributionType.MARITAL)],
            remainderDistributions: [],
            additionalDistributions: undefined
        },
        ...overrides
    }
}

export function generateIrrevocableTrust(overrides?: Partial<IrrevocableTrust>, trustId: string = "some-irrevtrust-id"): IrrevocableTrust {
    return {
        flowchartName: "Sample Flowchart",
        trustId,
        legalName: "Sample Legal Name",
        nickName: "Sample Nickname",
        trustCreationDate: moment("2024-09-01").toISOString(),
        tags: "Exempt",
        grantors: [
            {
                trustId,
                memberId: testGetFamilyMockData.primaryMember.id,
                memberType: MemberType.Grantor,
                familyMember: true
            }
        ],
        trustees: [
            {
                trustId,
                memberId: testGetFamilyMockData.primaryMember.id,
                memberType: MemberType.Trustee,
                familyMember: true
            }
        ],
        successors: [],
        beneficiaries: [
            {
                customName: "",
                familyMember: true,
                memberId: testGetFamilyMockData.partnerMember?.id,
                memberOrder: undefined,
                memberType: MemberType.Beneficiary,
                trustId,
            }
        ],
        funding: "Irrevocable Trust Funding",
        terminatesUponSpecificDeath: false,
        distributions: "Irrevocable Trust Distributions",
        terminationDetails: "Irrevocable Trust Termination",
        investmentAdvisor: "Sample Investment Advisor",
        trustProtector: "Sample Trust Protector",
        trustJurisdiction: "NY",
        ...overrides
    };
}

export function generateIrrevocableTrustWithTermination(overrides?: Partial<IrrevocableTrust>): IrrevocableTrust {
    return {
        flowchartName: "Sample Flowchart",
        trustId: "some-irrevtrust-id",
        legalName: "Sample Legal Name",
        nickName: "Sample Nickname",
        trustCreationDate: moment("2024-09-01").toISOString(),
        tags: "Exempt",
        grantors: [
            {
                trustId: "some-irrevtrust-id",
                memberId: testGetFamilyMockData.primaryMember.id,
                memberType: MemberType.Grantor,
                familyMember: true
            }
        ],
        trustees: [
            {
                trustId: "some-irrevtrust-id",
                memberId: testGetFamilyMockData.primaryMember.id,
                memberType: MemberType.Trustee,
                familyMember: true
            }
        ],
        successors: [],
        beneficiaries: [
            {
                customName: "",
                familyMember: true,
                memberId: testGetFamilyMockData.partnerMember?.id,
                memberOrder: undefined,
                memberType: MemberType.Beneficiary,
                trustId: undefined,
            }
        ],
        funding: "Irrevocable Trust Funding",
        terminatesUponSpecificDeath: true,
        firstDeathTerminationMember: {
            trustId: "some-irrevtrust-id",
            memberId: testGetFamilyMockData.primaryMember.id,
            memberType: MemberType.Successor,
            familyMember: true
        },
        distributions: "Irrevocable Trust Distributions",
        terminationDetails: "Irrevocable Trust Termination",
        investmentAdvisor: "Sample Investment Advisor",
        trustProtector: "Sample Trust Protector",
        trustJurisdiction: "NY",
        firstDeathBeneficiaries: {
            firstDeathDistributions: [generateCharity(), generateOutright()],
            remainderDistributions: []
        },
        ...overrides
    };
}

export function generateEmptyRevocableTrust(overrides?: Partial<RevocableTrust>): RevocableTrust {
    return {
        flowchartName: "",
        legalName: "",
        nickName: "",
        trustCreationDate: moment().toISOString(),
        recordOfAmendment: "",
        trustId: "some-revtrust-id",
        grantors: [],
        trustees: [],
        successors: [],
        funding: "",
        investmentAdvisor: "",
        trustProtector: "",
        trustJurisdiction: "",
        assumedFirstDeath: "",
        hasBeneficiariesAtFirstDeath: true,
        beneficiaries: {
            firstDeathDistributions: [],
            remainderDistributions: [],
            additionalDistributions: undefined
        },
        ...overrides
    }
}

export function generateRevocableTrustWithFirstDeathAndRemainder(overrides?: Partial<RevocableTrust>): RevocableTrust {
    return generateRevocableTrust({
        beneficiaries: {
            ...generateRevocableTrust().beneficiaries,
            firstDeathDistributions: [generateCharity({distributionId: "charity-1"}),
                generateOutright({distributionId: "outright-1"}),
                generateTrust(DistributionType.MARITAL), generateOutright({
                    ...generateOutright({distributionId: "outright-2"}),
                outrightMembers: [
                    {
                        customName: "",
                        trustId: "some-revtrust-id",
                        memberId: testGetFamilyMockData.primaryMember.id,
                        memberType: MemberType.Successor,
                        familyMember: true,
                        memberOrder: 0
                    },
                    {
                        customName: "Custom Member",
                        trustId: "some-revtrust-id",
                        memberId: "some-custom-member-id",
                        memberType: MemberType.Successor,
                        familyMember: false,
                        memberOrder: 1
                    }
                ],
            })],
            remainderDistributions: [generateCharity({distributionId: "charity-1"}),
                generateCharity({distributionId: "charity-2"}), generateTrust(DistributionType.IN_TRUST)],
            additionalDistributions: undefined
        },
        ...overrides
    });
}

export function generateRevocableTrustWithRemainder(overrides?: Partial<RevocableTrust>): RevocableTrust {
    return generateRevocableTrust({
        beneficiaries: {
            ...generateRevocableTrust().beneficiaries,
            firstDeathDistributions: [],
            remainderDistributions: [generateCharity()],
            additionalDistributions: undefined
        },
        ...overrides
    });
}


export function generateWill(overrides?: Partial<Will>): Will {
    return {
        trustId: "some-will-id",
        flowchartName: "Sample Will",
        legalName: "Sample Legal Name",
        nickName: "Sample Nickname",
        creationDate: moment("2024-09-01").toISOString(),
        recordOfAmendment: "Sample Record of Amendments",
        funding: "Sample Funding",
        exercisesOfPoa: "Sample Exercises of POA",
        personalProperty: "Sample Personal Property",
        bequests: "Sample Bequests",
        residuaryDistribution: "Sample Residuary Distribution",
        jurisdiction: "NY",
        pourOverWill: true,
        willOwner: "norman-waite-id",
        primaryWill: true,
        ...overrides
    }
}


export function generateCharity(overrides?: Partial<Charity>): Charity {
    return {
        name: "Charity: Sample Charity, Another Charity",
        distributionType: DistributionType.CHARITY,
        funding: "Sample Funding",
        additionalDetails: "Sample Additional Details",
        charityBeneficiaries: [{
            charityId: "charity-id-1",
            name: "Sample Charity"
        }, {
            charityId: "charity-id-1",
            name: "Another Charity"
        }],
        ...overrides
    };
}

export function generateOutright(overrides?: Partial<Outright>): Outright {
    return {
        distributionType: DistributionType.OUTRIGHT,
        outrightMembers: [{
            customName: "",
            trustId: "some-revtrust-id",
            memberId: testGetFamilyMockData.primaryMember.id,
            memberType: MemberType.Successor,
            familyMember: true,
            memberOrder: 0
        }],
        name: "member-1",
        funding: "Sample Funding",
        additionalDetails: "Sample Additional Details",
        ...overrides
    }
}

export function generateTrust(distributionType: DistributionType, overrides?: Partial<Trust>): Trust {
    return {
        distributionType: distributionType,
        name: "Jackie's Trust",
        tags: "Exempt",
        trustees: [
            {
                trustId: "some-revtrust-id",
                memberId: testGetFamilyMockData.primaryMember.id,
                memberType: MemberType.Trustee,
                familyMember: true
            }
        ] as Trustee[],
        successors: [] as Trustee[],
        beneficiaries: distributionType === DistributionType.MARITAL ? [{
            customName: "",
            familyMember: true,
            memberId: testGetFamilyMockData.partnerMember?.id,
            memberOrder: undefined,
            memberType: MemberType.Beneficiary,
            trustId: undefined,
        }] : [],
        funding: "",
        distributions: "distributions",
        termination: "",
        powersOfAppointment: [] as PowerOfAppointment[],
        ...overrides
    }
}

export function generateMiscellaneous(overrides?: Partial<Miscellaneous>): Miscellaneous {
    return {
        trustId: "some-wildcard-trust-id",
        flowchartName: "Sample Miscellaneous",
        name: "Sample Misc Name",
        estateToggleType: EstateToggle.IN_ESTATE,
        visualCardRepresentation: "Outright",
        beneficiaries: [
            {
                trustId: "misc-id",
                memberId: "id-2",
                memberType: MemberType.Beneficiary,
                familyMember: true,
                customName: "Jackie Waite"
            }
        ],
        funding: "Sample funding",
        additionalDetails: "Sample additional details",
        ...overrides
    };
}

export function generateNonTrustDistribution(distributionType: DistributionType, overrides?: Partial<Charity | Outright>): Charity | Outright {
    return {
        distributionType: distributionType,
        name: distributionType === DistributionType.OUTRIGHT ? "Outright: Norman Waite" : "Charity: Jackie's Charity",
        outrightMembers: distributionType === DistributionType.OUTRIGHT ? [{
            trustId: "some-revtrust-id",
            memberId: testGetFamilyMockData.partnerMember?.id,
            memberType: MemberType.Beneficiary,
            familyMember: true
        },] : undefined,
        funding: "Sample Funding",
        additionalDetails: "Sample Additional Details",
        charityBeneficiaries: distributionType === DistributionType.CHARITY ? [{
            charityId: "charity-id-1",
            name: "Jackie's Charity"
        }] : undefined,
        ...overrides
    }
}

export function generateEmptyStateTrust(overrides?: Partial<Trust>): Trust {
    return {
        distributionType: DistributionType.MARITAL,
        name: "",
        tags: "Exempt",
        trustees: [],
        successors: [],
        beneficiaries: [],
        funding: "",
        distributions: "",
        termination: "",
        powersOfAppointment: [],
        ...overrides
    }
}

export function generatePowerOfAppointment(overrides?: Partial<PowerOfAppointment>): PowerOfAppointment {
    return {
        type: PoaType.Limited,
        transferType: TransferType.Testamentary,
        powerHolder: "",
        permissibleAppointees: "",
        additionalDetails: "",
        poaOrder: 0,
        ...overrides
    }
}

export function getDistributionTypeAsString(type: DistributionType): string {
    switch (type) {
        case DistributionType.MARITAL:
            return "Marital Trust"
        case DistributionType.CREDIT_SHELTER:
            return "Credit Shelter Trust"
        case DistributionType.IN_TRUST:
            return "In Trust"
        case DistributionType.SURVIVOR:
            return "Survivor's Trust"
        case DistributionType.CHARITY:
            return "Charity"
        case DistributionType.OUTRIGHT:
            return "Outright"
        default:
            return ""
    }
}

export const testListOfStates = {
    IL: "Illinois",
    NY: "New York"
}

export const testGetFamilyMockData: MemberGroup = {
    primaryMember: {
        id: "id-1",
        firstName: "Norman",
        lastName: "Waite",
        lifeStatus: LifeStatus.Living,
        relationshipType: null,
        deceasedDate: null,
        age: 40
    },
    partnerMember: {
        id: "id-2",
        firstName: "Jackie",
        lastName: "Waite",
        lifeStatus: LifeStatus.Living,
        relationshipType: FamilyRelationshipType.SPOUSE,
        deceasedDate: null,
        age: 40
    },
    additionalMembers: [
        {
            id: "id-3",
            firstName: "Norman III",
            lastName: "Waite",
            lifeStatus: LifeStatus.Living,
            relationshipType: FamilyRelationshipType.CHILD,
            deceasedDate: null,
            age: 20
        },
        {
            id: "id-4",
            firstName: "Baby",
            lastName: "Waite",
            lifeStatus: LifeStatus.Living,
            relationshipType: FamilyRelationshipType.CHILD,
            deceasedDate: null,
            age: 1
        }
    ]
}

export const testGetLongFamilyMockData: MemberGroup = {
    ...testGetFamilyMockData,
    additionalMembers: [
        ...testGetFamilyMockData.additionalMembers,
        {
            id: "id-5",
            firstName: "Norman".repeat(4),
            lastName: "Waite".repeat(4),
            lifeStatus: LifeStatus.Living,
            deceasedDate: null,
            relationshipType: FamilyRelationshipType.OTHER,
            age: 86
        },
        {
            id: "id-6",
            firstName: "Baby".repeat(4),
            lastName: "Waite".repeat(4),
            deceasedDate: null,
            lifeStatus: LifeStatus.Living,
            relationshipType: FamilyRelationshipType.OTHER,
            age: 17
        },
        {
            id: "id-7",
            firstName: "Jackie".repeat(4),
            lastName: "Waite".repeat(4),
            lifeStatus: LifeStatus.Living,
            relationshipType: FamilyRelationshipType.OTHER,
            deceasedDate: null,
            age: 63
        },
        {
            id: "id-8",
            firstName: "Other1".repeat(4),
            lastName: "Waite".repeat(4),
            lifeStatus: LifeStatus.Living,
            relationshipType: FamilyRelationshipType.OTHER,
            deceasedDate: null,
            age: 45
        },
        {
            id: "id-9",
            firstName: "Other2".repeat(4),
            lastName: "Waite".repeat(4),
            relationshipType: FamilyRelationshipType.OTHER,
            deceasedDate: null,
            lifeStatus: LifeStatus.Living,
            age: 32
        },
        {
            id: "id-10",
            firstName: "Other3".repeat(4),
            lastName: "Waite".repeat(4),
            deceasedDate: null,
            relationshipType: FamilyRelationshipType.OTHER,
            lifeStatus: LifeStatus.Living,
            age: 27
        },
    ]
}

export function generateFiduciaries(overrides?: Partial<FiduciaryAssignment>): FiduciarySummary {
    return {
        primaryMember: {
            assignedToMemberId: "some-fiduciary-id",
            executors: [{
                customName: "",
                fiduciaryAssignmentId: undefined,
                memberId: testGetFamilyMockData.primaryMember.id,
                fiduciaryMemberType: FiduciaryMemberType.Executor,
                familyMember: true,
                memberOrder: 0
            },
                {
                    customName: "",
                    fiduciaryAssignmentId: undefined,
                    memberId: testGetFamilyMockData.partnerMember?.id,
                    fiduciaryMemberType: FiduciaryMemberType.Executor,
                    familyMember: true,
                    memberOrder: 1
                },
                {
                    customName: "",
                    fiduciaryAssignmentId: undefined,
                    memberId: testGetFamilyMockData.additionalMembers[0].id,
                    fiduciaryMemberType: FiduciaryMemberType.Executor,
                    familyMember: true,
                    memberOrder: 2
                }],
            ...overrides
        }, partnerMember: {
            assignedToMemberId: "some-fiduciary-id",
            executors: []
        }
    }
}

export function generateFiduciariesWithExecutorsAndGuardians(): FiduciarySummary {
    return {
        primaryMember: {
            ...emptyFiduciarySummary.primaryMember,
            fiduciaryAssignmentId: "primary-fiduciary-assignment-id",
            assignedToMemberId: testGetFamilyMockData.primaryMember.id,
            executors: [{
                fiduciaryAssignmentId: "primary-fiduciary-assignment-id",
                fiduciaryMemberType: FiduciaryMemberType.Executor,
                memberId: testGetFamilyMockData.additionalMembers[0].id,
                customName: "",
                familyMember: true,
                memberOrder: 0,
                id: "fiduciary-id"
            }],
            guardiansForMinors: [{
                fiduciaryAssignmentId: "primary-fiduciary-assignment-id",
                fiduciaryMemberType: FiduciaryMemberType.Guardian,
                memberId: testGetFamilyMockData.partnerMember?.id,
                customName: "",
                familyMember: true,
                memberOrder: 0,
                id: "fiduciary-id-guardian"
            }],
        },
        partnerMember: {
            ...emptyFiduciarySummary.partnerMember,
            fiduciaryAssignmentId: "partner-fiduciary-assignment-id",
            assignedToMemberId: testGetFamilyMockData.partnerMember!.id,
            executors: [{
                fiduciaryAssignmentId: "partner-fiduciary-assignment-id",
                fiduciaryMemberType: FiduciaryMemberType.Executor,
                memberId: testGetFamilyMockData.additionalMembers[1].id,
                customName: "",
                familyMember: true,
                memberOrder: 0,
                id: "fiduciary-id-2"
            }],
            guardiansForMinors: [{
                fiduciaryAssignmentId: "partner-fiduciary-assignment-id",
                fiduciaryMemberType: FiduciaryMemberType.Guardian,
                memberId: testGetFamilyMockData?.primaryMember.id,
                customName: "",
                familyMember: true,
                memberOrder: 0,
                id: "fiduciary-id-guardian-2"
            }],
        }
    }
}